// Owns the client-side editable cable-takeoff state. Loads/persists per-plan
// overrides to localStorage and derives the effective geometry + lengths from the
// agent's read-only base geometry. The 4 edit UIs only call the mutators here.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import {
  shortBoardLabel,
  type CableSourceMarker,
  type CableTakeoffGeometry,
  type CableTraySegment,
} from "../utils/fetchCableTakeoffPaths";
import type {
  CircuitPathResult,
  RouteWaypoint,
} from "../../ReviewPage/hooks/useCircuitCablePaths/types";
import {
  applyOverrides,
  hasAnyOverride,
  isEmptyDocument,
  EDIT_SCHEMA_VERSION,
  migrateStoredOverrides,
  adoptedDestinationId,
  type AddedCableSource,
  type AdoptedDevice,
  type StrandedEdge,
  type CableEditOverrides,
  type RemovalGroup,
} from "./cableEditGeometry";
import {
  deviceKeyOf,
  deviceKeysOf,
  readDeviceEdit,
  withoutDeviceKeys,
} from "./deviceKey";
import { liveAddedCircuits, mintCircuitId } from "./addedCircuits";
import type { AddedCableCircuits } from "./addedCircuits";
import type { DeviceAddress } from "./deviceKey";
import { requestRouting } from "./reroute";
import { fingerprintCircuit, fingerprintRouterInputs } from "./routedBilling";
import type { FingerprintWall, RoutedBilling } from "./routedBilling";
import type { RequestReroute } from "./reroute";
import { describeEditStep } from "./editStepSummary";
import { routeDestinations } from "./routeDestinations";
import type { FeedArea } from "./feedAreas";
import type { CableSystem } from "./cableSystems";
import type { EditStepSummary } from "./editStepSummary";
import { describeMainBoard } from "./mainBoard";
import { routeCacheStorageKey, useCircuitRouting, useRouteCache } from "./routeCache";
import type { CircuitRoutingState, RouteRequest } from "./routeCache";
import type { MainBoardAnswer, MainBoardState, MainBoardView } from "./mainBoard";
import type { BoardRoleAnswer, CircuitBoard } from "./circuitBoards";
import type { BoardFeed, ResolvedBoardFeed } from "./boardFeeds";
import type { DeviceCategory } from "./deviceCategories";
import type { AppCircuit, DerivedCableTakeoff, UnfedDevice } from "./cableEditGeometry";
import { readHeight, resolveHeights, writeHeight } from "./installationHeights";
import type { HeightTarget, ResolvedHeights, StoredHeights } from "./installationHeights";
import type { CircuitVertical, DeviceHeightProfile } from "./heightProfile";
import type { CircuitLegMetres, LegKind } from "./circuitLegs";
import { feederCircuitId } from "./feederCircuits";
import type { FeederCircuit } from "./feederCircuits";

/**
 * One run's before-and-after from a plan-wide recalculate.
 *
 * A recalculate moves metres across the whole plan, and he signs them, so it
 * says what it did rather than quietly rewriting the numbers.
 */
export interface PlanRerouteChange {
  circuitId: string;
  /** What it billed before, by the same engine that measured the after. */
  beforeM: number;
  afterM: number;
  routingMode: "tray" | "point_to_point";
  trayTypesRidden: string[];
}

/** Plan-interaction mode driven by the source/route edit UIs. */
export type CableEditMode = "none" | "source" | "route";

export interface AssignDevicesArgs {
  /** Devices that already have an address: on a run, or left without a board. */
  destinationIds: string[];
  /** Devices on no run at all; adopting them mints their path. */
  adoptions: Array<{ elementId: string; position: RouteWaypoint; name: string }>;
  /** Names the target circuit in the same step, e.g. after the rooms it covers. */
  name?: string;
  target:
    | { kind: "circuit"; circuitId: string }
    | {
        kind: "newCircuit";
        circuit: {
          key: string;
          sourceLabel: string;
          boardName: string;
          cableType: string;
          system?: CableSystem;
        };
      };
}

export interface UseEditableCableTakeoffArgs {
  projectId: string;
  annotationKey: string | null | undefined;
  baseGeometry: CableTakeoffGeometry | null;
  imageScaleFactor: number;
  /** Effective annotation-pixels-per-metre — derived from the shared page scale
   * (metersPerPdfPoint) in ReviewPageV3, so Cables and Kabelgruppen use ONE scale. */
  scalePxPerM: number;
  /** Which installation each published circuit belongs to, from the BoQ. */
  systemByCircuit?: Record<string, CableSystem | undefined>;
  /**
   * Which plan the router should read trays for. Absent means no recalculate:
   * the button is the only thing that needs it, and it refuses rather than
   * guessing a plan.
   */
  floorPlanId?: number;
  /**
   * The router. Defaults to the app's own; a test passes its own so the whole
   * surface — the ask, the pending state, the settle, the step back — can be
   * exercised without a server.
   *
   * An ARGUMENT rather than a setter on the result: a test-only method on a
   * production API is a thing production can call.
   */
  requestReroute?: RequestReroute;
  /**
   * The walls the router will read, as this app holds them.
   *
   * Only the STALENESS mark reads them here. Whether a run goes round a wall or
   * breaks through it is the server's decision, taken against the same rows —
   * this side only has to know that the answer on screen was measured before he
   * drew one.
   */
  walls?: FingerprintWall[];
  /**
   * Whether the tray allowlist behind `trays` can be trusted yet.
   *
   * The estimator's verdicts on which drawn types carry cable are fetched
   * separately from the plan, so for one round trip `trays` is the vocabulary's
   * answer alone. Marking the plan stale on that would blame him for a
   * fingerprint the fetch had not finished writing.
   */
  areTrayInputsLoaded?: boolean;
  /** The layout detector suggested walls for this page; absent or false means there is nothing to confirm. */
  hasDetectedWalls?: boolean;
  /** False while it is not yet known whether the detector suggested walls. */
  areWallInputsLoaded?: boolean;
  /**
   * The trays the router will read, as this app holds them.
   *
   * Needed here only to know when they have MOVED since the last plan-wide
   * recalculate: a tray edit changes no cable waypoint, so the per-run
   * fingerprint cannot see it and every run would look fresh while the metres
   * they imply had already changed.
   */
  trays?: CableTraySegment[];
}

export interface EditableCableTakeoff {
  /** Effective geometry with all overrides applied (null when no base). */
  derivedGeometry: CableTakeoffGeometry | null;
  /** Effective per-circuit total in metres = billed + vertical. */
  derivedLengthsByCircuit: Record<string, number>;
  /** Total metres across all circuits (sum of the effective per-circuit totals). */
  derivedTotalM: number;
  /**
   * The same measurement with NO overrides applied — our metres for the run's
   * own geometry.
   *
   * It exists because `derivedTotalM - baseTakeoff.total_m` is not what the
   * estimator changed. Two engines measure this cable: the run counts its graph
   * edges in render pixels, we count segments off the polylines in PDF points
   * and convert with the page scale HE calibrated. Any difference between them
   * lands the moment the first edit flips the headline onto our number, and
   * without this it was all printed as "your changes".
   */
  baselineTotalM: number;

  /** Routed length per circuit (geometry × scale), before the vertical metres. */
  measuredByCircuit: Record<string, number>;
  /**
   * What each circuit bills horizontally. Differs from `measured` by
   * the no-tray surcharge, which only the router can attribute.
   */
  billedByCircuit: Record<string, number>;
  /**
   * Runs he has edited since they were last routed. The stored figure is still
   * the one shown — it is wrong by his edit, where re-measuring is wrong by the
   * whole surcharge — so the card marks the run rather than swapping the number.
   */
  staleBilledCircuits: string[];
  /** Holes through drawn walls per run, as the router answered. */
  wallCrossingsByCircuit: Record<string, number>;
  /**
   * Runs to ask the router about: a stale billed figure, or a cable this code
   * invented. One mark, because his question is the same either way.
   */
  circuitsNeedingRoute: string[];
  /** Metres each circuit's cable spends going up and down. */
  verticalByCircuit: Record<string, CircuitVertical>;
  /** Where each device's cable rises and drops, per circuit. */
  heightProfilesByCircuit: Record<string, DeviceHeightProfile[]>;
  /** Each run's metres by leg: feed, between the switches, the lights, a Lüfter's own run. */
  legsByCircuit: Record<string, CircuitLegMetres>;
  /** His cable type per leg, where he set one. */
  cableTypeByLegByCircuit: Record<string, Partial<Record<LegKind, string>>>;
  /** Puts one leg of a run on its own type; null hands it back to the run's type. */
  setLegCableType: (circuitId: string, kind: LegKind, cableType: string | null) => void;
  /** Every height in force: his answer, else the default. */
  heights: ResolvedHeights;
  /** Only what he answered. */
  storedHeights: StoredHeights;
  /** Null clears his answer, so the default stands again. */
  setHeight: (target: HeightTarget, heightM: number | null) => void;
  /** Karl's heights step is settled, so the circuit walk may open. */
  heightsConfirmed: boolean;
  confirmHeights: () => void;
  /** Karl's tray step is settled. */
  trayHeightsConfirmed: boolean;
  confirmTrayHeights: () => void;
  /** Karl's walls step is settled. */
  wallsConfirmed: boolean;
  confirmWalls: () => void;

  sourceOverrides: Record<string, RouteWaypoint>;
  moveSource: (label: string, x: number, y: number) => void;
  resetSource: (label: string) => void;
  /** Hide a source from the plan (and drop its position override). */
  removeSource: (label: string) => void;
  /** Place a feed point the run never found. Returns the label it was given. */
  addSource: (name: string, position: RouteWaypoint) => string;
  /** Feed points added by hand, keyed by the label they were given. */
  addedSources: Record<string, AddedCableSource>;
  /**
   * Strikes a source AND re-points every circuit it fed at another one, in ONE
   * write. Two writes would be two steps back for a single act, and the state
   * between them is a plan with six circuits fed from nowhere.
   */
  removeSourceTo: (label: string, toSourceLabel: string) => void;
  /** Bring back a source that was hidden, and clear its position override. */
  restoreSource: (label: string) => void;
  /** Sources hidden from the plan, keyed by board marker label. */
  removedSources: Record<string, true>;
  /**
   * Every source the run published, BEFORE his edits. The derived geometry
   * drops the struck ones, and a struck board is the one he most needs to see —
   * both to put it back and to know why its circuits went quiet.
   */
  baseSources: CableSourceMarker[];
  /** Rename a source (display only), keyed by board marker label. */
  renameSource: (label: string, name: string) => void;
  /** Override display names for sources, keyed by board marker label. */
  sourceNames: Record<string, string>;

  /**
   * Creates a run the estimation never published — a way the schematic lists
   * and nothing was caught on. Returns the circuit id it was minted under, so
   * the caller can select it, or null when that id is already on this plan.
   */
  addCircuit: (circuit: {
    key: string;
    sourceLabel: string;
    boardName: string;
    cableType: string;
    system?: CableSystem;
    breaker?: string;
    description?: string;
  }) => string | null;
  /** Runs he created, keyed by the circuit id minted for them. */
  addedCircuits: AddedCableCircuits;
  /**
   * Drops a run he created AND re-points every device on it, in ONE write.
   * `toCircuitId` null leaves them where the estimation had them: a device
   * moved onto a run that no longer exists is fed from nowhere and drawn
   * nowhere, which is the struck-board defect in a new place.
   */
  removeCircuit: (circuitId: string, toCircuitId: string | null) => void;

  /** Cable type in force per circuit — the estimator's where he set one. */
  cableTypeByCircuit: Record<string, string>;
  setCableType: (circuitId: string, cableType: string) => void;
  /** Cable types he created for this plan. */
  addedCableTypes: string[];
  /** Also puts it on that run — or on one leg of it — when one is named, in the same write. */
  addCableType: (name: string, forCircuitId?: string, forLeg?: LegKind) => void;
  /** Takes back a type he created; the runs that carry it keep their spelling. */
  removeCableType: (name: string) => void;
  resetCableType: (circuitId: string) => void;
  /** Feed point in force per circuit, by board marker label. */
  feedPointByCircuit: Record<string, string>;
  setFeedPoint: (circuitId: string, sourceLabel: string) => void;
  resetFeedPoint: (circuitId: string) => void;
  /** Areas of the plan fed from one board, oldest first. */
  areaFeeds: FeedArea[];
  addAreaFeed: (area: FeedArea) => void;
  removeAreaFeed: (areaId: string) => void;
  /** Karl's board step is settled, so the circuit walk may open. */
  boardsConfirmed: boolean;
  /** Boards, heights, trays and walls are all confirmed: only now is anything routed or totalled. */
  isSetupDone: boolean;
  /** Which installation each circuit belongs to; absent reads as power. */
  systemByCircuit: Record<string, CableSystem | undefined>;
  /** Boards whose circuits he has checked, by marker label. */
  circuitBoardsChecked: string[];
  checkCircuitBoard: (label: string) => void;
  uncheckCircuitBoard: (label: string) => void;
  confirmBoards: () => void;
  reopenBoards: () => void;
  /** Which board is the main one, and who said so. */
  mainBoard: MainBoardState;
  /** The same, with the board's name and position for Karl and the plan. */
  mainBoardView: MainBoardView;
  setMainBoard: (answer: MainBoardAnswer) => void;
  clearMainBoard: () => void;
  /** Per board: feeds its own runs, or hangs off the main board. */
  boardRoleAnswers: Record<string, BoardRoleAnswer>;
  setBoardRole: (label: string, role: BoardRoleAnswer) => void;
  clearBoardRole: (label: string) => void;
  /** Which board feeds each circuit, and why. */
  circuitBoardByCircuit: Record<string, CircuitBoard>;
  /** His own names for circuits, by circuit id. */
  circuitNames: Record<string, string>;
  /** His own identifiers for circuits, by circuit id. */
  circuitKeyOverrides: Record<string, string>;
  /** Empty gives the circuit its printed or generated identifier back. */
  setCircuitKey: (circuitId: string, key: string) => void;
  /** An empty name gives the circuit its generated name back. */
  setCircuitName: (circuitId: string, name: string) => void;
  /** What each board feeds, and whether he said so. */
  feedsByBoard: Record<string, ResolvedBoardFeed>;
  setBoardFeeds: (label: string, feed: BoardFeed) => void;
  clearBoardFeeds: (label: string) => void;
  /** His stored answers only; a new object on every change. */
  storedBoardFeeds: Record<string, BoardFeed> | undefined;
  /** Puts a selection on one circuit — an existing one or one made for it — in ONE undo step. */
  assignDevices: (args: AssignDevicesArgs) => string | null;
  /** Several of those in ONE undo step, e.g. a circuit split into many. */
  assignDevicesBatch: (batches: AssignDevicesArgs[]) => Array<string | null>;
  /** Devices no board reaches; they get no cable until he gives them one. */
  unfedDevices: UnfedDevice[];
  /** Categories no board feeds, with the devices left without one. */
  unfedCategories: Partial<Record<DeviceCategory, string[]>>;
  unbilledCategories: DeviceCategory[];
  setCategoryUnbilled: (category: DeviceCategory, isUnbilled: boolean) => void;
  deviceCountsByBoard: DerivedCableTakeoff["deviceCountsByBoard"];
  appCircuits: Record<string, AppCircuit>;
  /** The cables that feed a board, by circuit id. */
  feederCircuits: Record<string, FeederCircuit>;
  /** Names the board that feeds this one; it is stored, so Karl does not ask it again. */
  setFeederFrom: (toLabel: string, fromLabel: string) => void;
  /** Its feeder comes from another floor, so it has none on this plan; false undoes that. */
  setFeederOffFloor: (toLabel: string, isOffFloor: boolean) => void;
  /** Boards he said are fed from another floor. */
  feederOffFloor: Record<string, true>;
  categoryByCircuit: Record<string, DeviceCategory | null>;
  /** Guessed cables waiting for the router, and what it is doing about them. */
  routeRequests: RouteRequest[];
  routing: CircuitRoutingState;

  /** Hand-drawn destination waypoints, keyed by destinationId. */
  routeOverrides: Record<string, RouteWaypoint[]>;
  setRoutePath: (destinationId: string, waypoints: RouteWaypoint[]) => void;
  /** Every path one gesture touched, in one write — so Back undoes it once. */
  /**
   * Every path a reshape re-derived, and — in the SAME write, so it is one step
   * back — the lengths that run has which no cable runs over. They cannot go in
   * `routeOverrides`, and leaving them out is what threw his drawn cable away.
   */
  setRoutePaths: (
    writes: { destinationId: string; waypoints: RouteWaypoint[] }[],
    coalesceKey: string,
    stranded?: { circuitId: string; edges: StrandedEdge[] },
  ) => void;
  resetRoute: (destinationId: string) => void;
  /**
   * Ask the router for this run's geometry again, with the devices it has NOW.
   *
   * Explicit and per-run on purpose: a removal invalidates every other path in
   * the circuit (one Steiner tree per circuit, solved whole), but geometry must
   * never change without him asking — see KARL.md. One write, so one step back.
   */
  rerouteCircuit: (circuitId: string) => Promise<void>;
  /** Re-ask the router about every run, or only the ones named. One request, one step back. */
  rerouteAllCircuits: (onlyCircuitIds?: string[]) => Promise<PlanRerouteChange[]>;
  isReroutingPlan: boolean;
  /** Trays have changed since the last plan-wide recalculate. */
  arePlanTraysStale: boolean;
  /** What the last plan-wide recalculate changed, until he dismisses it. */
  planRerouteSummary: PlanRerouteChange[] | null;
  dismissPlanRerouteSummary: () => void;
  /** The run waiting on the router, so the panel can hold still and say so. */
  reroutingCircuitId: string | null;

  groupOverrides: Record<string, string>;
  reassignDevice: (destinationId: string, toCircuitId: string) => void;
  /** Several devices onto one circuit, in one write — one step back. */
  reassignDevices: (destinationIds: string[], toCircuitId: string) => void;
  /**
   * Puts a device the run left on NO circuit onto one. Keyed on the run's
   * `element_id`: it has no destination until this mints one.
   */
  adoptDevice: (
    elementId: string,
    circuitId: string,
    position: RouteWaypoint,
    name: string,
  ) => void;
  /** Devices adopted onto a circuit by hand, keyed by the run's element id. */
  adoptedDevices: Record<string, AdoptedDevice>;
  /**
   * The run that last took a device, and a token that changes on EVERY move —
   * so moving two devices onto the same run confirms twice, and a batch that
   * moves twelve confirms once. Read by the plan to say where it landed.
   *
   * Deliberately NOT part of the override document: undo puts the devices back
   * without re-confirming, because undo's own feedback is the number changing.
   */
  deviceMovedTo: { circuitId: string; token: number } | null;
  resetGroup: (destinationId: string) => void;

  /** Devices removed from the takeoff (dropped from geometry + counts), keyed by destinationId. */
  removedDevices: Record<string, true>;
  /** Every removal action, oldest first, with what it struck. */
  removalGroups: RemovalGroup[];
  removeDevice: (destinationId: string) => void;
  /** Strikes a whole catch as ONE group. */
  removeDevices: (destinationIds: string[]) => void;
  restoreDevice: (destinationId: string) => void;
  restoreGroup: (groupId: string) => void;
  /** Seals a group once Karl has asked about it. */
  closeRemovalGroup: (groupId: string) => void;
  /** Strikes named devices under a group id chosen before the removal. */
  parkDevices: (groupId: string, destinationIds: string[]) => void;
  /** Restores a removal and moves everything it struck onto one run. */
  assignGroupTo: (groupId: string, toCircuitId: string) => void;
  /** The same for a single struck device, in one write so Back undoes it once. */
  assignDeviceTo: (destinationId: string, toCircuitId: string) => void;
  /** Positions (base/PDF space) of removed devices, for plan highlighting. */
  removedDeviceMarkers: {
    x: number;
    y: number;
    destinationId: string;
    circuitId: string;
  }[];
  /** Which board feeds each circuit — his feed point, else the run's guess. */
  feedingBoardByCircuit: Record<string, string | null>;
  /** Circuits whose feeding board was removed — kept in the list, struck out. */
  orphanedCircuitIds: string[];
  /** Devices left unfed by a struck-off board, for Karl to ask about. */
  orphanedDevices: {
    circuitCount: number;
    deviceCount: number;
    sourceLabels: string[];
  };

  resetAll: () => void;
  hasEdits: boolean;
  /** One step back through everything he has changed on this plan. */
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /**
   * What Undo would take back, and what Redo would put back — the difference
   * between two documents on the stack, so it cannot describe a step other than
   * the one it is about to replay. Null when that button has nothing to do, so
   * a disabled button can never carry the last label it had.
   */
  undoStep: EditStepSummary | null;
  redoStep: EditStepSummary | null;
  /**
   * The step Undo or Redo has just replayed, so the plan can say where it
   * landed. A token, not a bare summary: two steps back over the same run must
   * mark twice.
   *
   * Deliberately NOT part of the override document, like `deviceMovedTo`: it is
   * a live handshake with the drawing, not something to persist.
   */
  steppedTo: { summary: EditStepSummary; token: number } | null;
}

// Long enough that a pointer-rate drag writes once at the end, short enough
// that a reload right after an edit still finds it.
const PERSIST_DEBOUNCE_MS = 400;

/** localStorage key for a plan's cable edits. */
export function cableEditStorageKey(
  projectId: string,
  annotationKey: string,
): string {
  return `cables-edit:${projectId}:${annotationKey}`;
}

/**
 * Its own key, never the edits'. Twenty whole documents is the big write, and a
 * quota failure while saving the history must not be able to cost him the edits
 * themselves — which is what one shared blob would risk.
 */
export function cableEditHistoryStorageKey(
  projectId: string,
  annotationKey: string,
): string {
  return `cables-edit-history:${projectId}:${annotationKey}`;
}

// Frozen and hoisted: a fresh `[]` per render is a new identity, and consumers
// key memos and effects on this.
const EMPTY_REMOVAL_GROUPS: RemovalGroup[] = [];
Object.freeze(EMPTY_REMOVAL_GROUPS);

/** Deep enough for a morning's work, short enough to stay small in memory. */
const UNDO_DEPTH = 60;
/**
 * What survives a reload. Shallower than the in-session stack on purpose: 60 was
 * sized against memory, this one shares a 5 MB origin quota with the edits, and
 * what he needs back after a reload is the last few things he did.
 */
const PERSISTED_UNDO_DEPTH = 20;
const HISTORY_STORAGE_VERSION = 1;

interface EditHistory {
  past: CableEditOverrides[];
  future: CableEditOverrides[];
}

// Frozen and hoisted: consumers key memos on this, and a fresh {} per render is
// a new identity.
const EMPTY_ADOPTED: Record<string, AdoptedDevice> = {};
Object.freeze(EMPTY_ADOPTED);
const NO_SOURCES: CableSourceMarker[] = [];
Object.freeze(NO_SOURCES);

// Frozen: consumers key memos and effects on this identity.
const NO_AREAS: FeedArea[] = [];
const NO_CHECKED_BOARDS: string[] = [];
const NO_ROLE_ANSWERS: Record<string, BoardRoleAnswer> = {};
const NO_CATEGORIES: DeviceCategory[] = [];
const NO_CIRCUIT_NAMES: Record<string, string> = {};
const NO_SYSTEMS: Record<string, CableSystem | undefined> = {};
Object.freeze(NO_AREAS);

// Frozen: read through a ref, so the identity must not change per render.
const NO_FINGERPRINT_WALLS: FingerprintWall[] = [];
Object.freeze(NO_FINGERPRINT_WALLS);

/** The empty override set, for measuring the run's own geometry our way. */
const NO_OVERRIDES: CableEditOverrides = {};
Object.freeze(NO_OVERRIDES);

/**
 * Where a device's edits are written: its annotation element id when the run
 * published one, else the run-scoped address as before.
 *
 * The address book is the run's OWN geometry, so a device the run never drew a
 * path for (an adopted one) falls back to its address — which already embeds
 * the element id and is durable by construction.
 */
const addressIn = (
  book: Record<string, DeviceAddress>,
  destinationId: string,
): DeviceAddress => book[destinationId] ?? { destinationId };
const NO_DOCUMENTS: CableEditOverrides[] = [];
Object.freeze(NO_DOCUMENTS);
const EMPTY_HISTORY: EditHistory = Object.freeze({
  past: NO_DOCUMENTS,
  future: NO_DOCUMENTS,
});

interface StoredHistory {
  v: number;
  past: CableEditOverrides[];
  future: CableEditOverrides[];
}

/**
 * Every stored document goes through the SAME migration the live one does: a
 * history entry is an override document, so a stack written by an older build
 * carries exactly the legacy route keys `migrateStoredOverrides` exists to fix.
 */
function loadHistory(historyKey: string | null): EditHistory {
  if (!historyKey) return EMPTY_HISTORY;
  try {
    const raw = window.localStorage.getItem(historyKey);
    if (!raw) return EMPTY_HISTORY;
    const parsed = JSON.parse(raw) as StoredHistory;
    if (parsed?.v !== HISTORY_STORAGE_VERSION) return EMPTY_HISTORY;
    if (!Array.isArray(parsed.past) || !Array.isArray(parsed.future))
      return EMPTY_HISTORY;
    return {
      past: parsed.past.map(migrateStoredOverrides),
      future: parsed.future.map(migrateStoredOverrides),
    };
  } catch {
    return EMPTY_HISTORY;
  }
}

function loadOverrides(storageKey: string | null): CableEditOverrides {
  if (!storageKey) return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    return migrateStoredOverrides(JSON.parse(raw) as CableEditOverrides);
  } catch {
    return {};
  }
}

// The whole circuit as the derive will measure it: a device the router did not answer for keeps its cable,
// and a fingerprint over the written paths alone never matched it, so the run read as changed forever.
const NO_CABLE_TYPES: string[] = [];
const NO_OFF_FLOOR: Record<string, true> = {};

const withWrites = (
  paths: CircuitPathResult[],
  writes: { destinationId: string; waypoints: RouteWaypoint[] }[],
): CircuitPathResult[] => {
  const written = new Map(writes.map((write) => [write.destinationId, write.waypoints]));
  return paths.map((path) => {
    const waypoints = written.get(path.destinationId);
    return waypoints ? { ...path, waypoints } : path;
  });
};

export function useEditableCableTakeoff({
  projectId,
  annotationKey,
  baseGeometry,
  imageScaleFactor,
  scalePxPerM,
  systemByCircuit: publishedSystemByCircuit = NO_SYSTEMS,
  floorPlanId,
  requestReroute: injectedRequestReroute,
  trays,
  walls,
  areTrayInputsLoaded = true,
  hasDetectedWalls = false,
  areWallInputsLoaded = true,
}: UseEditableCableTakeoffArgs): EditableCableTakeoff {
  const storageKey = annotationKey
    ? cableEditStorageKey(projectId, annotationKey)
    : null;
  const historyKey = annotationKey
    ? cableEditHistoryStorageKey(projectId, annotationKey)
    : null;

  const [overrides, setOverridesState] = useState<CableEditOverrides>(() =>
    loadOverrides(storageKey),
  );
  const loadedKeyRef = useRef<string | null>(storageKey);
  // Read by addSource so it can pick a free label without depending on
  // `overrides` and rebuilding every consumer's callbacks on each edit.
  const overridesRef = useRef<CableEditOverrides>(overrides);
  overridesRef.current = overrides;

  // Whole documents, not diffs: an override document is small, and a stack of
  // them is the only thing that undoes a device removal, a reshape and a feed
  // point with one rule instead of three.
  // Restored, not empty. The overrides come back from localStorage on mount, so
  // a history that did not would leave him looking at a panel full of his own
  // edits with no way to step back — *"and where is the undo/redo buttons?"*
  const [history, setHistory] = useState<EditHistory>(() =>
    loadHistory(historyKey),
  );
  const historyRef = useRef<EditHistory>(history);
  historyRef.current = history;
  // The last coalescing key, so a drag is ONE step back rather than one per
  // mouse move.
  const coalesceRef = useRef<string | null>(null);
  // Every way a device joins a run goes through `noteMove`, so the confirmation
  // cannot be forgotten on the next one that is added.
  const moveTokenRef = useRef(0);
  const [deviceMovedTo, setDeviceMovedTo] = useState<{
    circuitId: string;
    token: number;
  } | null>(null);
  const noteMove = useCallback((circuitId: string) => {
    moveTokenRef.current += 1;
    setDeviceMovedTo({ circuitId, token: moveTokenRef.current });
  }, []);
  // Its own token, and its own state: a move confirms a device joining a run,
  // a step confirms a step. One field would make Redo of a reassignment
  // indistinguishable from making it.
  const stepTokenRef = useRef(0);
  const [steppedTo, setSteppedTo] = useState<{
    summary: EditStepSummary;
    token: number;
  } | null>(null);
  // The board feeding each circuit, as last derived. Read by `removeSourceTo`,
  // which must know what leaves with a board before it strikes it.
  const feedingRef = useRef<Record<string, string | null>>({});

  /**
   * Every edit goes through here. Nothing computes inside the state updater:
   * React may run an updater twice, and a history stack that grows on a replay
   * is a Back button that lies.
   */
  const setOverrides = useCallback(
    (
      updater:
        | CableEditOverrides
        | ((current: CableEditOverrides) => CableEditOverrides),
      coalesceKey?: string,
    ): void => {
      const previous = overridesRef.current;
      const next = typeof updater === "function" ? updater(previous) : updater;
      if (next === previous) return;

      const held = historyRef.current;
      const isSameGesture =
        Boolean(coalesceKey) &&
        coalesceKey === coalesceRef.current &&
        held.past.length > 0;
      const nextHistory: EditHistory = {
        past: isSameGesture
          ? held.past
          : [...held.past, previous].slice(-UNDO_DEPTH),
        future: NO_DOCUMENTS,
      };

      coalesceRef.current = coalesceKey ?? null;
      overridesRef.current = next;
      historyRef.current = nextHistory;
      setHistory(nextHistory);
      setOverridesState(next);
    },
    [],
  );

  const step = useCallback((back: boolean): void => {
    const held = historyRef.current;
    const from = back ? held.past : held.future;
    if (!from.length) return;
    const current = overridesRef.current;
    const target = back ? from[from.length - 1] : from[0];
    const nextHistory: EditHistory = back
      ? {
          past: held.past.slice(0, -1),
          future: [current, ...held.future].slice(0, UNDO_DEPTH),
        }
      : {
          past: [...held.past, current].slice(-UNDO_DEPTH),
          future: held.future.slice(1),
        };

    // Computed here, outside every state updater, for the same reason the
    // history is: an updater React runs twice must not mark the plan twice.
    const summary = describeEditStep(
      back ? { from: target, to: current } : { from: current, to: target },
    );
    stepTokenRef.current += 1;

    coalesceRef.current = null;
    overridesRef.current = target;
    historyRef.current = nextHistory;
    setHistory(nextHistory);
    setOverridesState(target);
    setSteppedTo({ summary, token: stepTokenRef.current });
  }, []);

  const undo = useCallback(() => step(true), [step]);
  const redo = useCallback(() => step(false), [step]);

  /**
   * One diff per edit, not per render: both depend only on the document and the
   * stack, and each walks the keys of the ONE map the step changed — every
   * other field settles on a reference check.
   */
  const undoStep = useMemo(
    () =>
      history.past.length
        ? describeEditStep({
            from: history.past[history.past.length - 1],
            to: overrides,
          })
        : null,
    [history.past, overrides],
  );
  const redoStep = useMemo(
    () =>
      history.future.length
        ? describeEditStep({ from: overrides, to: history.future[0] })
        : null,
    [history.future, overrides],
  );

  // Reload overrides whenever the plan (storage key) changes. Not an edit, and
  // the other plan's steps must not be reachable from this one.
  useEffect(() => {
    const loaded = loadOverrides(storageKey);
    const loadedHistory = loadHistory(historyKey);
    overridesRef.current = loaded;
    historyRef.current = loadedHistory;
    coalesceRef.current = null;
    setHistory(loadedHistory);
    setOverridesState(loaded);
    // The other plan's step is not a thing to confirm on this one's drawing.
    setSteppedTo(null);
    loadedKeyRef.current = storageKey;
  }, [storageKey, historyKey]);

  // Held in a ref so the flush below always writes the newest document without
  // making the flush effect re-run on every keystroke of a drag.
  const persistRef = useRef<() => void>(() => undefined);
  persistRef.current = () => {
    if (!storageKey || loadedKeyRef.current !== storageKey) return;
    try {
      if (!isEmptyDocument(overrides)) {
        window.localStorage.setItem(
          storageKey,
          JSON.stringify({ ...overrides, schemaVersion: EDIT_SCHEMA_VERSION }),
        );
      } else {
        window.localStorage.removeItem(storageKey);
      }
    } catch {
      /* ignore quota / disabled storage */
    }
    if (!historyKey) return;
    // Second, and in its own try: if this one throws on quota the edits above
    // are already safely written.
    try {
      const held = historyRef.current;
      if (held.past.length || held.future.length) {
        const stored: StoredHistory = {
          v: HISTORY_STORAGE_VERSION,
          past: held.past.slice(-PERSISTED_UNDO_DEPTH),
          future: held.future.slice(0, PERSISTED_UNDO_DEPTH),
        };
        window.localStorage.setItem(historyKey, JSON.stringify(stored));
      } else {
        window.localStorage.removeItem(historyKey);
      }
    } catch {
      /* ignore quota / disabled storage */
    }
  };

  // Persist after a change settles, not on every one. Dragging a route node
  // writes an override on every mouse move, and stringifying the whole document
  // into localStorage synchronously at pointer rate is felt as a heavy drag.
  useEffect(() => {
    if (!storageKey || loadedKeyRef.current !== storageKey) return;
    const timer = window.setTimeout(
      () => persistRef.current(),
      PERSIST_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [storageKey, overrides]);

  // The last edit before the plan changes or the tab closes would otherwise sit
  // in a timer that never fires. Keyed on the storage key alone, so it flushes
  // when the estimator leaves — not on every change.
  useEffect(() => () => persistRef.current(), [storageKey]);

  const moveSource = useCallback(
    (label: string, x: number, y: number) => {
      setOverrides((o) => ({
        ...o,
        sourceOverrides: { ...o.sourceOverrides, [label]: { x, y } },
      }));
    },
    [setOverrides],
  );

  const resetSource = useCallback(
    (label: string) => {
      setOverrides((o) => {
        if (!o.sourceOverrides?.[label]) return o;
        const next = { ...o.sourceOverrides };
        delete next[label];
        return { ...o, sourceOverrides: next };
      });
    },
    [setOverrides],
  );

  /**
   * Every path a gesture touched, in ONE write under ONE key.
   *
   * A run is one cable, so a drag moves every path sharing the trunk. Writing
   * them one at a time alternated coalescing keys and defeated the coalescing
   * itself — a two-path drag over three mouse moves cost SIX steps back, and
   * Back only ever undid a frame of it.
   */
  // destinationId -> how that device is remembered. Held in a ref so the setters
  // below keep their identities: every one of them is a `useCallback` with no
  // deps by design, and this must not be the thing that changes that.
  const addressBook = useMemo(() => {
    const book: Record<string, DeviceAddress> = {};
    for (const path of baseGeometry?.all.paths ?? []) {
      book[path.destinationId] = {
        destinationId: path.destinationId,
        elementId: path.elementId,
      };
    }
    return book;
  }, [baseGeometry]);
  const addressRef = useRef(addressBook);
  addressRef.current = addressBook;

  // The geometry as it stands, for the async actions below: they read it when
  // the answer comes back, not when the click happened.
  const derivedRef = useRef<CableTakeoffGeometry | null>(null);
  // What each run bills right now, read when a batch starts so the summary
  // can say what the recalculate actually bought.
  const billedBeforeRef = useRef<Record<string, number>>({});
  const traysRef = useRef<CableTraySegment[]>([]);
  const wallsRef = useRef<FingerprintWall[]>(NO_FINGERPRINT_WALLS);
  // The types the tab is DRAWING. Sent with every request so the router rides
  // exactly the steel on screen — one allowlist, both sides.
  const trayTypesRef = useRef<string[]>([]);
  const hasDetectedWallsRef = useRef(hasDetectedWalls);

  const setRoutePaths = useCallback(
    (
      writes: { destinationId: string; waypoints: RouteWaypoint[] }[],
      coalesceKey: string,
      stranded?: { circuitId: string; edges: StrandedEdge[] },
      routed?: Array<{ circuitId: string; billing: RoutedBilling }>,
      trayFingerprint?: string,
    ) => {
      if (!writes.length && !stranded) return;
      setOverrides((o) => {
        const routeOverrides = { ...o.routeOverrides };
        for (const write of writes) {
          const address = addressIn(addressRef.current, write.destinationId);
          routeOverrides[deviceKeyOf(address)] = write.waypoints;
        }
        // Written in the SAME update as the waypoints, so the geometry and the
        // figure measured from it are one step back, never two.
        const withRouted = routed?.length
          ? {
              ...o,
              routeOverrides,
              routedByCircuit: {
                ...o.routedByCircuit,
                ...Object.fromEntries(
                  routed.map((one) => [one.circuitId, one.billing]),
                ),
              },
              ...(trayFingerprint
                ? { routedTrayFingerprint: trayFingerprint }
                : {}),
            }
          : { ...o, routeOverrides };
        if (!stranded) return withRouted;
        const strandedByCircuit = { ...o.strandedByCircuit };
        // An empty list is a DELETION, not an empty entry: once he has joined
        // the last drawn length up to a device it is ordinary cable, and a
        // leftover key would keep re-adding an edge the graph already has.
        if (stranded.edges.length) {
          strandedByCircuit[stranded.circuitId] = stranded.edges;
        } else {
          delete strandedByCircuit[stranded.circuitId];
        }
        return { ...withRouted, strandedByCircuit };
      }, coalesceKey);
    },
    [setOverrides],
  );

  const setRoutePath = useCallback(
    (destinationId: string, waypoints: RouteWaypoint[]) => {
      setRoutePaths([{ destinationId, waypoints }], `route:${destinationId}`);
    },
    [setRoutePaths],
  );

  // The app's own router by default; a test injects its own through this ref.
  // The stub stays exported for exactly that - it answers "nothing changed",
  // which is the honest reply when there is no server to ask.
  const requestReroute = useRef<RequestReroute>(
    injectedRequestReroute ?? requestRouting,
  );
  requestReroute.current = injectedRequestReroute ?? requestRouting;
  const [reroutingCircuitId, setReroutingCircuitId] = useState<string | null>(
    null,
  );

  const rerouteCircuit = useCallback(
    async (circuitId: string) => {
      const geometry = derivedRef.current;
      const state = geometry?.byCircuit[circuitId];
      if (!geometry || !state) return;
      // Where the run starts NOW — his feed point if he moved it.
      const source = state.paths[0]?.waypoints[0];
      if (!source) return;
      // Addressed by element, never by index: the router answers about devices,
      // and a positional address is not something it can be asked about.
      const destinations = routeDestinations({
        paths: state.paths,
        deviceTypeByElementId: geometry.deviceTypeByElementId,
      });
      if (!destinations.length) return;

      setReroutingCircuitId(circuitId);
      try {
        if (floorPlanId === undefined) return;
        // The run's own Zuschlag for this circuit. Asking the router at 0%
        // would undercut every run the run itself surcharged.
        const noTraySurchargePct =
          baseGeometry?.defaults?.[circuitId]?.noTraySurchargePct ?? 0;
        const answer = await requestReroute.current({
          // The trays are NOT sent. The server reads them from
          // `quantity_surveyor_routes`, so a tray he drew a moment ago is in
          // the answer — which is the whole reason routing moved here.
          floorPlanId,
          noTraySurchargePct,
          trayTypes: trayTypesRef.current,
          circuits: [{ ref: circuitId, trayType: "", source, destinations }],
        });
        const routed = answer.circuits.find((one) => one.ref === circuitId);
        const paths = routed?.paths;
        // An empty path list claims nothing. Writing a billed figure of 0
        // against it would zero the run on a failed request.
        if (!routed || !paths?.length) return;
        // Through the same write every hand edit uses, so it coalesces into one
        // undo step and lands in `routeOverrides` under the durable key.
        const byElement = new Map(
          state.paths
            .filter((path) => path.elementId)
            .map((path) => [path.elementId as string, path.destinationId]),
        );
        const writes = paths
          .map((path) => {
            const destinationId = byElement.get(path.elementId);
            return destinationId
              ? { destinationId, waypoints: path.waypoints }
              : null;
          })
          .filter(
            (
              write,
            ): write is { destinationId: string; waypoints: RouteWaypoint[] } =>
              Boolean(write),
          );
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_RUN_REROUTED, {
          metresBefore: billedBeforeRef.current[circuitId] ?? 0,
          metresAfter: routed.billedM,
        });
        setRoutePaths(writes, `reroute:${circuitId}`, undefined, [
          {
            circuitId,
            billing: {
              billedM: routed.billedM,
              onTrayM: routed.onTrayM,
              routingMode: routed.routingMode,
              trayTypesRidden: routed.trayTypesRidden,
              wallCrossings: routed.wallCrossings,
              // Stamped from the waypoints we are about to WRITE, not from the
              // geometry still on screen: the fingerprint has to describe the
              // cable the figure was measured from, and that is the new one.
              fingerprint: fingerprintCircuit(withWrites(state.paths, writes)),
            },
          },
        ]);
      } finally {
        setReroutingCircuitId(null);
      }
    },
    [setRoutePaths, baseGeometry?.defaults, floorPlanId],
  );

  const [isReroutingPlan, setIsReroutingPlan] = useState(false);
  const [planRerouteSummary, setPlanRerouteSummary] = useState<
    PlanRerouteChange[] | null
  >(null);

  /**
   * Ask the router about EVERY run on the plan, in one request and one step back.
   *
   * Per-run is not enough once he has touched the trays: `weldTray` is not
   * length-neutral and welding is plan-global, so one new tray edge can change
   * which candidate network wins for circuits nowhere near it. The only honest
   * answer to a tray edit is to re-ask about all of them.
   *
   * It is still a BUTTON. The metres move and he signs them, so nothing here
   * runs as a consequence of drawing a tray — see the per-run rule this follows.
   */
  const rerouteAllCircuits = useCallback(async (onlyCircuitIds?: string[]): Promise<
    PlanRerouteChange[]
  > => {
    const geometry = derivedRef.current;
    if (!geometry || floorPlanId === undefined) return [];
    const only = onlyCircuitIds ? new Set(onlyCircuitIds) : null;

    const asked = Object.entries(geometry.byCircuit)
      .filter(([circuitId]) => !only || only.has(circuitId))
      .map(([circuitId, state]) => {
        const source = state.paths[0]?.waypoints[0];
        if (!source) return null;
        const destinations = routeDestinations({
          paths: state.paths,
          deviceTypeByElementId: geometry.deviceTypeByElementId,
        });
        return destinations.length
          ? {
              circuitId,
              state,
              request: { ref: circuitId, trayType: "", source, destinations },
            }
          : null;
      })
      .filter((one): one is NonNullable<typeof one> => Boolean(one));
    if (!asked.length) return [];

    // One Zuschlag for the request, because the endpoint prices a batch at one
    // rate. It is a PROJECT parameter that the run happens to stamp per circuit,
    // so the plan's own value is whichever the runs agree on.
    const noTraySurchargePct =
      asked
        .map(
          (one) => baseGeometry?.defaults?.[one.circuitId]?.noTraySurchargePct,
        )
        .find((pct) => pct !== undefined) ?? 0;

    // Read BEFORE the request: what the router is about to answer for is the
    // network as it stands now, not as it stands when the reply lands.
    const routerInputsNow = fingerprintRouterInputs({
      trays: traysRef.current,
      walls: wallsRef.current,
    });

    setIsReroutingPlan(true);
    try {
      const answer = await requestReroute.current({
        floorPlanId,
        noTraySurchargePct,
        trayTypes: trayTypesRef.current,
        circuits: asked.map((one) => one.request),
      });
      const byRef = new Map(answer.circuits.map((one) => [one.ref, one]));

      const writes: { destinationId: string; waypoints: RouteWaypoint[] }[] =
        [];
      const routed: { circuitId: string; billing: RoutedBilling }[] = [];
      const changes: PlanRerouteChange[] = [];

      for (const one of asked) {
        const reply = byRef.get(one.circuitId);
        // An empty path list claims nothing, so that run is left exactly as it
        // was — not zeroed, and not counted as a change.
        if (!reply?.paths.length) continue;
        const byElement = new Map(
          one.state.paths
            .filter((path) => path.elementId)
            .map((path) => [path.elementId as string, path.destinationId]),
        );
        const circuitWrites = reply.paths
          .map((path) => {
            const destinationId = byElement.get(path.elementId);
            return destinationId
              ? { destinationId, waypoints: path.waypoints }
              : null;
          })
          .filter(
            (
              write,
            ): write is { destinationId: string; waypoints: RouteWaypoint[] } =>
              Boolean(write),
          );
        if (!circuitWrites.length) continue;
        writes.push(...circuitWrites);
        routed.push({
          circuitId: one.circuitId,
          billing: {
            billedM: reply.billedM,
            onTrayM: reply.onTrayM,
            routingMode: reply.routingMode,
            trayTypesRidden: reply.trayTypesRidden,
            wallCrossings: reply.wallCrossings,
            fingerprint: fingerprintCircuit(withWrites(one.state.paths, circuitWrites)),
          },
        });
        changes.push({
          circuitId: one.circuitId,
          beforeM: billedBeforeRef.current[one.circuitId] ?? 0,
          afterM: reply.billedM,
          routingMode: reply.routingMode,
          trayTypesRidden: reply.trayTypesRidden,
        });
      }

      if (writes.length) {
        // ONE coalesce key for the whole plan, so a recalculate he did not like
        // is one step back and not eighty-three. The tray fingerprint rides the
        // same write, or a step back would leave the plan claiming it had been
        // recalculated against trays it no longer matches.
        // Only a whole-plan answer may claim the plan matches its trays; a few runs re-asked do not.
        setRoutePaths(writes, "reroute:plan", undefined, routed, only ? undefined : routerInputsNow);
      }
      setPlanRerouteSummary(changes);
      return changes;
    } finally {
      setIsReroutingPlan(false);
    }
  }, [baseGeometry, floorPlanId, setRoutePaths]);

  /**
   * The trays have moved since the plan was last recalculated, so the metres
   * were measured against a different drawing.
   *
   * Never true before the first recalculate: a plan nobody has asked the router
   * about is not stale, it is simply the run's own answer, and marking all 83
   * runs on open would be noise rather than a finding.
   */
  const arePlanTraysStale = useMemo(() => {
    const stamped = overrides.routedTrayFingerprint;
    if (!stamped) return false;
    // Nothing is stale until the trays are the real trays. A type he REFUSED
    // still counts as one until his verdicts land, and an amber mark he did
    // nothing to earn teaches him to ignore the amber mark.
    if (!areTrayInputsLoaded) return false;
    // Trays AND walls: both are what the router reads about the building, and a
    // wall he draws changes where the cable can go every bit as much as a tray
    // does. Without the walls in here the whole plan looked fresh the moment he
    // drew one, and nothing on the panel asked him to recalculate.
    return stamped !== fingerprintRouterInputs({ trays: trays ?? [], walls });
  }, [overrides.routedTrayFingerprint, trays, walls, areTrayInputsLoaded]);

  const dismissPlanRerouteSummary = useCallback(
    () => setPlanRerouteSummary(null),
    [],
  );

  const resetRoute = useCallback(
    (destinationId: string) => {
      setOverrides((o) => {
        const address = addressIn(addressRef.current, destinationId);
        if (!readDeviceEdit({ edits: o.routeOverrides, address })) return o;
        return {
          ...o,
          routeOverrides: withoutDeviceKeys({
            edits: o.routeOverrides,
            address,
          }),
        };
      });
    },
    [setOverrides],
  );

  const setCableType = useCallback(
    (circuitId: string, cableType: string) => {
      setOverrides((o) => ({
        ...o,
        cableTypeByCircuit: { ...o.cableTypeByCircuit, [circuitId]: cableType },
      }));
    },
    [setOverrides],
  );

  const resetCableType = useCallback(
    (circuitId: string) => {
      setOverrides((o) => {
        if (o.cableTypeByCircuit?.[circuitId] === undefined) return o;
        const next = { ...o.cableTypeByCircuit };
        delete next[circuitId];
        return { ...o, cableTypeByCircuit: next };
      });
    },
    [setOverrides],
  );

  // Stored as given, even when it names a board that is not on the plan:
  // applyOverrides degrades an unknown label to the derived board, and dropping
  // it here would lose the answer if the board comes back.
  const setFeedPoint = useCallback(
    (circuitId: string, sourceLabel: string) => {
      setOverrides((o) => ({
        ...o,
        feedPointByCircuit: {
          ...o.feedPointByCircuit,
          [circuitId]: sourceLabel,
        },
      }));
    },
    [setOverrides],
  );

  const setFeederFrom = useCallback(
    (toLabel: string, fromLabel: string) => {
      const id = feederCircuitId(toLabel);
      setOverrides((o) =>
        o.feedPointByCircuit?.[id] === fromLabel
          ? o
          : { ...o, feedPointByCircuit: { ...o.feedPointByCircuit, [id]: fromLabel } },
      );
    },
    [setOverrides],
  );

  const setFeederOffFloor = useCallback(
    (toLabel: string, isOffFloor: boolean) => {
      setOverrides((o) => {
        if (Boolean(o.feederOffFloor?.[toLabel]) === isOffFloor) return o;
        const { [toLabel]: _was, ...rest } = o.feederOffFloor ?? {};
        return { ...o, feederOffFloor: isOffFloor ? { ...rest, [toLabel]: true } : rest };
      });
    },
    [setOverrides],
  );

  const addAreaFeed = useCallback(
    (area: FeedArea) => {
      setOverrides((o) => ({
        ...o,
        areaFeeds: [...(o.areaFeeds ?? []), area],
      }));
    },
    [setOverrides],
  );

  // The first calculation routes on the trays as they stand, so a later tray edit reads as a change.
  const stampedWhenSetupDone = useCallback(
    (o: CableEditOverrides): CableEditOverrides => {
      const hasTrays = trayTypesRef.current.length > 0;
      const isDone =
        o.boardsConfirmed &&
        o.heightsConfirmed &&
        (o.trayHeightsConfirmed || !hasTrays) &&
        (o.wallsConfirmed || !hasDetectedWallsRef.current);
      if (!isDone || o.routedTrayFingerprint) return o;
      return {
        ...o,
        routedTrayFingerprint: fingerprintRouterInputs({ trays: traysRef.current, walls: wallsRef.current }),
      };
    },
    [],
  );

  // The boards step is a hard gate; it must not open on a main board nobody confirmed.
  const mainBoardRef = useRef<MainBoardState>({ kind: "none" });
  const confirmBoards = useCallback(() => {
    const main = mainBoardRef.current.kind;
    if (main !== "answered" && main !== "note") return;
    setOverrides((o) => (o.boardsConfirmed ? o : stampedWhenSetupDone({ ...o, boardsConfirmed: true })));
  }, [setOverrides, stampedWhenSetupDone]);

  const setMainBoard = useCallback(
    (answer: MainBoardAnswer) => {
      setOverrides((o) =>
        o.mainBoard?.label === answer.label && o.mainBoard.by === answer.by
          ? o
          : { ...o, mainBoard: answer },
      );
    },
    [setOverrides],
  );

  const clearMainBoard = useCallback(() => {
    setOverrides((o) => {
      if (!o.mainBoard) return o;
      const { mainBoard: _cleared, ...rest } = o;
      return rest;
    });
  }, [setOverrides]);

  const setBoardRole = useCallback(
    (label: string, role: BoardRoleAnswer) => {
      setOverrides((o) =>
        o.boardRoleAnswers?.[label] === role
          ? o
          : { ...o, boardRoleAnswers: { ...o.boardRoleAnswers, [label]: role } },
      );
    },
    [setOverrides],
  );

  const setCircuitName = useCallback(
    (circuitId: string, name: string) => {
      const trimmed = name.trim();
      setOverrides((o) => {
        if ((o.circuitNames?.[circuitId] ?? "") === trimmed) return o;
        const { [circuitId]: _previous, ...rest } = o.circuitNames ?? {};
        return { ...o, circuitNames: trimmed ? { ...rest, [circuitId]: trimmed } : rest };
      });
    },
    [setOverrides],
  );

  const setCircuitKey = useCallback(
    (circuitId: string, key: string) => {
      const trimmed = key.trim();
      setOverrides((o) => {
        if ((o.circuitKeys?.[circuitId] ?? "") === trimmed) return o;
        const { [circuitId]: _previous, ...rest } = o.circuitKeys ?? {};
        return { ...o, circuitKeys: trimmed ? { ...rest, [circuitId]: trimmed } : rest };
      });
    },
    [setOverrides],
  );

  const setBoardFeeds = useCallback(
    (label: string, feed: BoardFeed) => {
      setOverrides((o) => ({ ...o, boardFeeds: { ...o.boardFeeds, [label]: feed } }));
    },
    [setOverrides],
  );

  const clearBoardFeeds = useCallback(
    (label: string) => {
      setOverrides((o) => {
        if (!o.boardFeeds?.[label]) return o;
        const { [label]: _cleared, ...rest } = o.boardFeeds;
        return { ...o, boardFeeds: rest };
      });
    },
    [setOverrides],
  );

  const setCategoryUnbilled = useCallback(
    (category: DeviceCategory, isUnbilled: boolean) => {
      setOverrides((o) => {
        const current = o.unbilledCategories ?? [];
        if (current.includes(category) === isUnbilled) return o;
        return {
          ...o,
          unbilledCategories: isUnbilled
            ? [...current, category]
            : current.filter((one) => one !== category),
        };
      });
    },
    [setOverrides],
  );

  const clearBoardRole = useCallback(
    (label: string) => {
      setOverrides((o) => {
        if (!o.boardRoleAnswers?.[label]) return o;
        const { [label]: _cleared, ...rest } = o.boardRoleAnswers;
        return { ...o, boardRoleAnswers: rest };
      });
    },
    [setOverrides],
  );

  // The heights and the circuit step sit behind the boards, so reopening the boards reopens them too.
  const reopenBoards = useCallback(() => {
    setOverrides((o) =>
      o.boardsConfirmed ||
      o.heightsConfirmed ||
      o.trayHeightsConfirmed ||
      o.wallsConfirmed ||
      (o.circuitBoardsChecked ?? []).length
        ? {
            ...o,
            boardsConfirmed: false,
            heightsConfirmed: false,
            trayHeightsConfirmed: false,
            wallsConfirmed: false,
            circuitBoardsChecked: [],
          }
        : o,
    );
  }, [setOverrides]);

  const checkCircuitBoard = useCallback(
    (label: string) => {
      setOverrides((o) => {
        const checked = o.circuitBoardsChecked ?? [];
        if (checked.includes(label)) return o;
        return { ...o, circuitBoardsChecked: [...checked, label] };
      });
    },
    [setOverrides],
  );

  const uncheckCircuitBoard = useCallback(
    (label: string) => {
      setOverrides((o) => {
        const checked = o.circuitBoardsChecked ?? [];
        if (!checked.includes(label)) return o;
        return {
          ...o,
          circuitBoardsChecked: checked.filter((one) => one !== label),
        };
      });
    },
    [setOverrides],
  );

  const removeAreaFeed = useCallback(
    (areaId: string) => {
      setOverrides((o) => {
        const areas = o.areaFeeds ?? [];
        if (!areas.some((one) => one.id === areaId)) return o;
        return { ...o, areaFeeds: areas.filter((one) => one.id !== areaId) };
      });
    },
    [setOverrides],
  );

  const resetFeedPoint = useCallback(
    (circuitId: string) => {
      setOverrides((o) => {
        if (o.feedPointByCircuit?.[circuitId] === undefined) return o;
        const next = { ...o.feedPointByCircuit };
        delete next[circuitId];
        return { ...o, feedPointByCircuit: next };
      });
    },
    [setOverrides],
  );

  const reassignDevice = useCallback(
    (destinationId: string, toCircuitId: string) => {
      setOverrides((o) => ({
        ...o,
        groupOverrides: {
          ...o.groupOverrides,
          [deviceKeyOf(addressIn(addressRef.current, destinationId))]:
            toCircuitId,
        },
      }));
      noteMove(toCircuitId);
    },
    [noteMove, setOverrides],
  );

  const reassignDevices = useCallback(
    (destinationIds: string[], toCircuitId: string) => {
      if (!destinationIds.length) return;
      setOverrides((o) => {
        const next = { ...o.groupOverrides };
        destinationIds.forEach((destinationId) => {
          next[deviceKeyOf(addressIn(addressRef.current, destinationId))] =
            toCircuitId;
        });
        return { ...o, groupOverrides: next };
      });
      noteMove(toCircuitId);
    },
    [noteMove, setOverrides],
  );

  const resetGroup = useCallback(
    (destinationId: string) => {
      setOverrides((o) => {
        const address = addressIn(addressRef.current, destinationId);
        if (!readDeviceEdit({ edits: o.groupOverrides, address })) return o;
        return {
          ...o,
          groupOverrides: withoutDeviceKeys({
            edits: o.groupOverrides,
            address,
          }),
        };
      });
    },
    [setOverrides],
  );

  // A device put back on its own leaves its group; a group with nothing left in
  // it stops being something he can look at.
  const withoutFromGroups = (
    o: CableEditOverrides,
    addresses: DeviceAddress[],
  ): Pick<CableEditOverrides, "removalGroups"> => {
    // Both keys: a group written before the durable key remembers the address,
    // and a device he cannot get back out of a group is a device he cannot get
    // back at all.
    const gone = new Set(addresses.flatMap((address) => deviceKeysOf(address)));
    const groups = (o.removalGroups ?? [])
      .map((group) => ({
        ...group,
        destinationIds: group.destinationIds.filter((id) => !gone.has(id)),
      }))
      .filter((group) => group.destinationIds.length > 0);
    return { removalGroups: groups };
  };

  // One action, one group — a box that struck twelve devices is one thing he
  // removed, not twelve. A flat list of 32 struck devices with no sense of what
  // came out together is what made the strip unreadable.
  const strike = useCallback(
    (addresses: DeviceAddress[], joinOpen: boolean) => {
      setOverrides((o) => {
        const fresh = addresses
          .filter(
            (address) => !readDeviceEdit({ edits: o.removedDevices, address }),
          )
          .map(deviceKeyOf);
        if (!fresh.length) return o;
        const removedDevices = { ...o.removedDevices };
        for (const id of fresh) removedDevices[id] = true as const;

        const groups = o.removalGroups ?? [];
        const last = groups[groups.length - 1];
        if (joinOpen && last && !last.closed) {
          return {
            ...o,
            removedDevices,
            removalGroups: [
              ...groups.slice(0, -1),
              { ...last, destinationIds: [...last.destinationIds, ...fresh] },
            ],
          };
        }

        const seq = (o.removalSeq ?? 0) + 1;
        return {
          ...o,
          removedDevices,
          removalSeq: seq,
          removalGroups: [
            ...groups,
            { id: `rm-${seq}`, destinationIds: fresh },
          ],
        };
      });
    },
    [setOverrides],
  );

  // Under an id chosen BEFORE the removal, so one answer can strike the devices
  // and arm the follow-up in the same move — a minted id is not known until the
  // write has already happened.
  const parkDevices = useCallback(
    (groupId: string, destinationIds: string[]) => {
      setOverrides((o) => {
        const fresh = destinationIds
          .map((id) => addressIn(addressRef.current, id))
          .filter(
            (address) => !readDeviceEdit({ edits: o.removedDevices, address }),
          )
          .map(deviceKeyOf);
        if (!fresh.length) return o;
        const removedDevices = { ...o.removedDevices };
        for (const id of fresh) removedDevices[id] = true as const;
        const groups = o.removalGroups ?? [];
        const held = groups.find((g) => g.id === groupId);
        return {
          ...o,
          removedDevices,
          removalGroups: held
            ? groups.map((g) =>
                g.id === groupId
                  ? { ...g, destinationIds: [...g.destinationIds, ...fresh] }
                  : g,
              )
            : [...groups, { id: groupId, destinationIds: fresh }],
        };
      });
    },
    [setOverrides],
  );

  // Karl has put the question; nothing new may join it after that.
  const closeRemovalGroup = useCallback(
    (groupId: string) => {
      setOverrides((o) => {
        const groups = o.removalGroups ?? [];
        if (!groups.some((g) => g.id === groupId && !g.closed)) return o;
        return {
          ...o,
          removalGroups: groups.map((g) =>
            g.id === groupId ? { ...g, closed: true } : g,
          ),
        };
      });
    },
    [setOverrides],
  );

  // A box is one action however many it caught, so it always gets its own group.
  const removeDevices = useCallback(
    (destinationIds: string[]) =>
      strike(
        destinationIds.map((id) => addressIn(addressRef.current, id)),
        false,
      ),
    [strike],
  );

  // Striking devices off a row one at a time is ONE thing he is doing, and it
  // has to be one question — twelve clicks were becoming twelve asks. It joins
  // whatever group Karl has not yet put a question about.
  const removeDevice = useCallback(
    (destinationId: string) =>
      strike([addressIn(addressRef.current, destinationId)], true),
    [strike],
  );

  const restoreDevice = useCallback(
    (destinationId: string) => {
      setOverrides((o) => {
        const address = addressIn(addressRef.current, destinationId);
        if (!readDeviceEdit({ edits: o.removedDevices, address })) return o;
        return {
          ...o,
          removedDevices: withoutDeviceKeys({
            edits: o.removedDevices,
            address,
          }),
          ...withoutFromGroups(o, [address]),
        };
      });
    },
    [setOverrides],
  );

  // Put back AND re-parented in one write. `applyOverrides` creates the target
  // run if it does not exist yet and re-anchors each path to that board, so the
  // metres are recomputed from the geometry rather than carried over.
  const assignGroupTo = useCallback(
    (groupId: string, toCircuitId: string) => {
      noteMove(toCircuitId);
      setOverrides((o) => {
        const group = (o.removalGroups ?? []).find((g) => g.id === groupId);
        if (!group) return o;
        const removedDevices = { ...o.removedDevices };
        const groupOverrides = { ...o.groupOverrides };
        for (const id of group.destinationIds) {
          delete removedDevices[id];
          groupOverrides[id] = toCircuitId;
        }
        return {
          ...o,
          removedDevices,
          groupOverrides,
          removalGroups: (o.removalGroups ?? []).filter(
            (g) => g.id !== groupId,
          ),
        };
      });
    },
    [noteMove, setOverrides],
  );

  // ONE write, so it is one step back. Restoring then reassigning would put two
  // entries on the undo stack for a single act, and Back would only half undo it.
  const assignDeviceTo = useCallback(
    (destinationId: string, toCircuitId: string) => {
      setOverrides((o) => {
        const address = addressIn(addressRef.current, destinationId);
        if (!readDeviceEdit({ edits: o.removedDevices, address })) return o;
        return {
          ...o,
          removedDevices: withoutDeviceKeys({
            edits: o.removedDevices,
            address,
          }),
          groupOverrides: {
            ...o.groupOverrides,
            [deviceKeyOf(address)]: toCircuitId,
          },
          ...withoutFromGroups(o, [address]),
        };
      });
      noteMove(toCircuitId);
    },
    [noteMove, setOverrides],
  );

  // ONE write, like every other device move, so Back undoes an adoption in one
  // step. Nothing is computed in the updater: the position and name are handed
  // in already resolved.
  const adoptDevice = useCallback(
    (
      elementId: string,
      circuitId: string,
      position: RouteWaypoint,
      name: string,
    ) => {
      setOverrides((o) => ({
        ...o,
        adoptedDevices: {
          ...o.adoptedDevices,
          [elementId]: { circuitId, position, name },
        },
        // An adopted device he had struck and is now adopting again must not
        // stay struck, or the mint below would be skipped and nothing appears.
        ...(o.removedDevices?.[adoptedDestinationId(elementId)]
          ? {
              removedDevices: Object.fromEntries(
                Object.entries(o.removedDevices).filter(
                  ([id]) => id !== adoptedDestinationId(elementId),
                ),
              ) as Record<string, true>,
            }
          : {}),
      }));
      noteMove(circuitId);
    },
    [noteMove, setOverrides],
  );

  // Every batch lands in ONE write: an automatic split into sixteen circuits is one step back.
  const assignDevicesBatch = useCallback(
    (batches: AssignDevicesArgs[]): Array<string | null> => {
      const taken = new Set([
        ...Object.keys(baseGeometry?.byCircuit ?? {}),
        ...Object.keys(overridesRef.current.addedCircuits ?? {}),
      ]);
      const planned = batches.map(({ destinationIds, adoptions, target, name }) => {
        if (!destinationIds.length && !adoptions.length) return null;
        if (target.kind === "circuit") {
          return { circuitId: target.circuitId, fresh: null, destinationIds, adoptions, name };
        }
        const fresh = {
          ...target.circuit,
          key: target.circuit.key.trim(),
          cableType: target.circuit.cableType.trim(),
        };
        const circuitId = mintCircuitId({ key: fresh.key, boardName: fresh.boardName });
        if (!fresh.key || taken.has(circuitId)) return null;
        taken.add(circuitId);
        return { circuitId, fresh, destinationIds, adoptions, name };
      });
      const writes = planned.filter((one): one is NonNullable<typeof one> => one !== null);
      if (!writes.length) return planned.map(() => null);
      setOverrides((o) => {
        const groupOverrides = { ...o.groupOverrides };
        const adoptedDevices = { ...o.adoptedDevices };
        const removedDevices = { ...o.removedDevices };
        const addedCircuits = { ...o.addedCircuits };
        const circuitNames = { ...o.circuitNames };
        writes.forEach(({ circuitId, fresh, destinationIds, adoptions, name }) => {
          if (fresh) addedCircuits[circuitId] = fresh;
          if (name?.trim()) circuitNames[circuitId] = name.trim();
          destinationIds.forEach((destinationId) => {
            groupOverrides[deviceKeyOf(addressIn(addressRef.current, destinationId))] = circuitId;
          });
          adoptions.forEach(({ elementId, position, name }) => {
            adoptedDevices[elementId] = { circuitId, position, name };
            delete removedDevices[adoptedDestinationId(elementId)];
          });
        });
        return { ...o, groupOverrides, adoptedDevices, removedDevices, addedCircuits, circuitNames };
      });
      noteMove(writes[writes.length - 1].circuitId);
      return planned.map((one) => one?.circuitId ?? null);
    },
    [baseGeometry, noteMove, setOverrides],
  );

  const assignDevices = useCallback(
    (args: AssignDevicesArgs): string | null => assignDevicesBatch([args])[0],
    [assignDevicesBatch],
  );

  const restoreGroup = useCallback(
    (groupId: string) => {
      setOverrides((o) => {
        const group = (o.removalGroups ?? []).find((g) => g.id === groupId);
        if (!group) return o;
        const next = { ...o.removedDevices };
        for (const id of group.destinationIds) delete next[id];
        return {
          ...o,
          removedDevices: next,
          removalGroups: (o.removalGroups ?? []).filter(
            (g) => g.id !== groupId,
          ),
        };
      });
    },
    [setOverrides],
  );

  // Making a type for a run is one act, so it is one write and one step back.
  const addCableType = useCallback(
    (name: string, forCircuitId?: string, forLeg?: LegKind) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      setOverrides((o) => {
        const known = (o.cableTypes ?? []).includes(trimmed);
        if (known && !forCircuitId) return o;
        const placed = !forCircuitId
          ? {}
          : forLeg
            ? {
                cableTypeByLeg: {
                  ...o.cableTypeByLeg,
                  [forCircuitId]: { ...o.cableTypeByLeg?.[forCircuitId], [forLeg]: trimmed },
                },
              }
            : { cableTypeByCircuit: { ...o.cableTypeByCircuit, [forCircuitId]: trimmed } };
        return {
          ...o,
          cableTypes: known ? o.cableTypes : [...(o.cableTypes ?? []), trimmed],
          ...placed,
        };
      });
    },
    [setOverrides],
  );

  const setLegCableType = useCallback(
    (circuitId: string, kind: LegKind, cableType: string | null) => {
      setOverrides((o) => {
        const { [kind]: _previous, ...rest } = o.cableTypeByLeg?.[circuitId] ?? {};
        const legs = cableType ? { ...rest, [kind]: cableType } : rest;
        const { [circuitId]: _circuit, ...others } = o.cableTypeByLeg ?? {};
        return {
          ...o,
          cableTypeByLeg: Object.keys(legs).length ? { ...others, [circuitId]: legs } : others,
        };
      });
    },
    [setOverrides],
  );

  const removeCableType = useCallback(
    (name: string) => {
      setOverrides((o) =>
        (o.cableTypes ?? []).includes(name)
          ? { ...o, cableTypes: (o.cableTypes ?? []).filter((type) => type !== name) }
          : o,
      );
    },
    [setOverrides],
  );

  const setHeight = useCallback(
    (target: HeightTarget, heightM: number | null) => {
      setOverrides((o) =>
        readHeight({ stored: o, target }) === (heightM ?? undefined)
          ? o
          : { ...o, ...writeHeight({ stored: o, target, heightM }) },
      );
    },
    [setOverrides],
  );

  const confirmHeights = useCallback(() => {
    setOverrides((o) => (o.heightsConfirmed ? o : stampedWhenSetupDone({ ...o, heightsConfirmed: true })));
  }, [setOverrides, stampedWhenSetupDone]);

  const confirmTrayHeights = useCallback(() => {
    setOverrides((o) =>
      o.trayHeightsConfirmed ? o : stampedWhenSetupDone({ ...o, trayHeightsConfirmed: true }),
    );
  }, [setOverrides, stampedWhenSetupDone]);

  const confirmWalls = useCallback(() => {
    setOverrides((o) => (o.wallsConfirmed ? o : stampedWhenSetupDone({ ...o, wallsConfirmed: true })));
  }, [setOverrides, stampedWhenSetupDone]);

  const removeSource = useCallback(
    (label: string) => {
      setOverrides((o) => {
        const removedSources = { ...o.removedSources, [label]: true as const };
        const sourceOverrides = { ...o.sourceOverrides };
        delete sourceOverrides[label];
        return { ...o, removedSources, sourceOverrides };
      });
    },
    [setOverrides],
  );

  // Which circuits leave with the board is read OUT here, from the derived map,
  // and never inside the updater: React may run an updater twice, and a second
  // pass would read a document where the board is already struck and re-point
  // nothing.
  const removeSourceTo = useCallback(
    (label: string, toSourceLabel: string) => {
      if (toSourceLabel === label) return;
      const fed = Object.entries(feedingRef.current)
        .filter(([, board]) => board === label)
        .map(([circuitId]) => circuitId);
      setOverrides((o) => {
        const removedSources = { ...o.removedSources, [label]: true as const };
        const sourceOverrides = { ...o.sourceOverrides };
        delete sourceOverrides[label];
        const feedPointByCircuit = { ...o.feedPointByCircuit };
        fed.forEach((circuitId) => {
          feedPointByCircuit[circuitId] = toSourceLabel;
        });
        return { ...o, removedSources, sourceOverrides, feedPointByCircuit };
      });
    },
    [setOverrides],
  );

  // Labels must stay unique: they key every source override, so a collision
  // would silently move or hide the board the estimator already had.
  const addSource = useCallback(
    (name: string, position: RouteWaypoint): string => {
      const base = shortBoardLabel(name.trim() || "S");
      let label = base;
      let n = 2;
      const taken = new Set([
        ...(baseGeometry?.sources ?? []).map((source) => source.label),
        ...Object.keys(overridesRef.current.addedSources ?? {}),
      ]);
      while (taken.has(label)) label = `${base}${n++}`;
      // The steps stay done: a board added afterwards is asked about on its own, and the circuits stay on screen.
      setOverrides((o) => ({
        ...o,
        addedSources: {
          ...o.addedSources,
          [label]: { name: name.trim() || label, x: position.x, y: position.y },
        },
      }));
      return label;
    },
    [baseGeometry, setOverrides],
  );

  const restoreSource = useCallback(
    (label: string) => {
      setOverrides((o) => {
        if (!o.removedSources?.[label]) return o;
        const removedSources = { ...o.removedSources };
        delete removedSources[label];
        return { ...o, removedSources };
      });
    },
    [setOverrides],
  );

  // The only mutator that brings a CIRCUIT into existence. It is one write, so
  // Back undoes creating a run in one step, and nothing is computed inside the
  // updater — the id is minted before it, because a second pass would mint the
  // same one and the guard above it would then refuse his own creation.
  const addCircuit = useCallback(
    (circuit: {
      key: string;
      sourceLabel: string;
      boardName: string;
      cableType: string;
      system?: CableSystem;
      breaker?: string;
      description?: string;
    }): string | null => {
      const key = circuit.key.trim();
      if (!key) return null;
      const circuitId = mintCircuitId({ key, boardName: circuit.boardName });
      // Two rows for one way is a total nobody can reconcile — and there is
      // nothing to create either way: the estimation's own circuit already
      // takes devices under exactly this id.
      if (baseGeometry?.byCircuit[circuitId]) return null;
      if (overridesRef.current.addedCircuits?.[circuitId]) return null;
      const trimmedBreaker = circuit.breaker?.trim();
      const trimmedDescription = circuit.description?.trim();
      setOverrides((o) => ({
        ...o,
        addedCircuits: {
          ...o.addedCircuits,
          [circuitId]: {
            key,
            sourceLabel: circuit.sourceLabel,
            boardName: circuit.boardName,
            cableType: circuit.cableType.trim(),
            ...(circuit.system ? { system: circuit.system } : {}),
            ...(trimmedBreaker ? { breaker: trimmedBreaker } : {}),
            ...(trimmedDescription ? { description: trimmedDescription } : {}),
          },
        },
      }));
      return circuitId;
    },
    [baseGeometry, setOverrides],
  );

  // ONE write, the same rule `removeSourceTo` follows: the run goes and its
  // devices are re-pointed together. Two writes would be two steps back for a
  // single act, and the state between them is a plan with devices on a circuit
  // that no longer exists — drawn nowhere and counted nowhere.
  const removeCircuit = useCallback(
    (circuitId: string, toCircuitId: string | null) => {
      setOverrides((o) => {
        if (!o.addedCircuits?.[circuitId]) return o;
        const addedCircuits = { ...o.addedCircuits };
        delete addedCircuits[circuitId];

        // A device MOVED onto it goes where he says, or back to the run the
        // estimation had it on — never left pointing at a circuit that is gone.
        const groupOverrides = { ...o.groupOverrides };
        for (const [deviceKey, target] of Object.entries(groupOverrides)) {
          if (target !== circuitId) continue;
          if (toCircuitId) groupOverrides[deviceKey] = toCircuitId;
          else delete groupOverrides[deviceKey];
        }

        // A device ADOPTED onto it has nowhere to go back to: the estimation
        // put it on no circuit at all, so dropping the adoption returns it to
        // the unrouted list, which is exactly where it came from.
        const adoptedDevices = { ...o.adoptedDevices };
        for (const [elementId, adopted] of Object.entries(adoptedDevices)) {
          if (adopted.circuitId !== circuitId) continue;
          if (toCircuitId)
            adoptedDevices[elementId] = { ...adopted, circuitId: toCircuitId };
          else delete adoptedDevices[elementId];
        }

        // Everything keyed on the circuit goes with it. Left behind, his drop
        // or his cable type would silently re-attach to a run the estimation
        // publishes under that id later — an edit he never made to it.
        const withoutCircuit = <T,>(
          edits: Record<string, T> | undefined,
        ): Record<string, T> | undefined => {
          if (!edits || !(circuitId in edits)) return edits;
          const next = { ...edits };
          delete next[circuitId];
          return next;
        };

        return {
          ...o,
          addedCircuits,
          groupOverrides,
          adoptedDevices,
          cableTypeByCircuit: withoutCircuit(o.cableTypeByCircuit),
          cableTypeByLeg: withoutCircuit(o.cableTypeByLeg),
          feedPointByCircuit: withoutCircuit(o.feedPointByCircuit),
          routedByCircuit: withoutCircuit(o.routedByCircuit),
          strandedByCircuit: withoutCircuit(o.strandedByCircuit),
        };
      });
      if (toCircuitId) noteMove(toCircuitId);
    },
    [noteMove, setOverrides],
  );

  const renameSource = useCallback(
    (label: string, name: string) => {
      setOverrides((o) => {
        const next = { ...o.sourceNames };
        if (name.trim()) next[label] = name;
        else delete next[label];
        return { ...o, sourceNames: next };
      });
    },
    [setOverrides],
  );

  const resetAll = useCallback(() => setOverrides({}), [setOverrides]);

  // The LIVE ones — a created run the estimation has since published itself is
  // stood down, and the list must never show a row the geometry has dropped.
  const addedCircuits = useMemo(
    () =>
      liveAddedCircuits({
        addedCircuits: overrides.addedCircuits,
        baseCircuitIds: Object.keys(baseGeometry?.byCircuit ?? {}),
      }),
    [overrides.addedCircuits, baseGeometry],
  );

  // His own runs answer the same question: a safety run he created is not a zone's to take.
  const systemByCircuit = useMemo(() => {
    const added = overrides.addedCircuits ?? {};
    if (!Object.keys(added).length) return publishedSystemByCircuit;
    const byCircuit = { ...publishedSystemByCircuit };
    Object.entries(added).forEach(([circuitId, circuit]) => {
      byCircuit[circuitId] = circuit.system;
    });
    return byCircuit;
  }, [publishedSystemByCircuit, overrides.addedCircuits]);

  const trayTypes = useMemo(
    () => [...new Set((trays ?? []).map((tray) => tray.type))].sort(),
    [trays],
  );
  const isSetupDone =
    Boolean(overrides.boardsConfirmed) &&
    Boolean(overrides.heightsConfirmed) &&
    areTrayInputsLoaded &&
    (Boolean(overrides.trayHeightsConfirmed) || trayTypes.length === 0) &&
    areWallInputsLoaded &&
    (Boolean(overrides.wallsConfirmed) || !hasDetectedWalls);
  const routeCache = useRouteCache(
    annotationKey ? routeCacheStorageKey(projectId, annotationKey) : null,
  );
  const derived = useMemo(() => {
    if (!baseGeometry) {
      return {
        derivedGeometry: null as CableTakeoffGeometry | null,
        derivedLengthsByCircuit: {} as Record<string, number>,
        measuredByCircuit: {} as Record<string, number>,
        billedByCircuit: {} as Record<string, number>,
        staleBilledCircuits: [] as string[],
        wallCrossingsByCircuit: {} as Record<string, number>,
        circuitsNeedingRoute: [] as string[],
        verticalByCircuit: {} as Record<string, CircuitVertical>,
        heightProfilesByCircuit: {} as Record<string, DeviceHeightProfile[]>,
        legsByCircuit: {} as Record<string, CircuitLegMetres>,
        cableTypeByLegByCircuit: {} as Record<string, Partial<Record<LegKind, string>>>,
        resolvedFeedPointByCircuit: {} as Record<string, string>,
        resolvedCableTypeByCircuit: {} as Record<string, string>,
        derivedTotalM: 0,
        removedDeviceMarkers:
          [] as EditableCableTakeoff["removedDeviceMarkers"],
        feedingBoardByCircuit: {} as Record<string, string | null>,
        circuitBoardByCircuit: {} as Record<string, CircuitBoard>,
        mainBoard: { kind: "none" } as MainBoardState,
        routeRequests: [] as RouteRequest[],
        feedsByBoard: {} as Record<string, ResolvedBoardFeed>,
        unfedCategories: {} as Partial<Record<DeviceCategory, string[]>>,
        deviceCountsByBoard: {} as DerivedCableTakeoff["deviceCountsByBoard"],
        appCircuits: {} as Record<string, AppCircuit>,
        feederCircuits: {} as Record<string, FeederCircuit>,
        categoryByCircuit: {} as Record<string, DeviceCategory | null>,
        unfedDevices: [] as UnfedDevice[],
        orphanedCircuitIds: [] as string[],
        orphanedDevices: {
          circuitCount: 0,
          deviceCount: 0,
          sourceLabels: [] as string[],
        },
      };
    }
    const d = applyOverrides(
      baseGeometry,
      overrides,
      imageScaleFactor,
      scalePxPerM,
      systemByCircuit,
      { cache: routeCache.cache, routeAll: isSetupDone, trays },
    );
    return {
      derivedGeometry: d.geometry,
      derivedLengthsByCircuit: d.lengthsByCircuit,
      measuredByCircuit: d.measuredByCircuit,
      billedByCircuit: d.billedByCircuit,
      staleBilledCircuits: d.staleBilledCircuits,
      wallCrossingsByCircuit: d.wallCrossingsByCircuit,
      circuitsNeedingRoute: d.circuitsNeedingRoute,
      verticalByCircuit: d.verticalByCircuit,
      heightProfilesByCircuit: d.heightProfilesByCircuit,
      legsByCircuit: d.legsByCircuit,
      cableTypeByLegByCircuit: d.cableTypeByLegByCircuit,
      // Re-addressed onto the circuits in THIS geometry, not read off the raw
      // document: an edit stored under a circuit id the run has since renamed is
      // still his, and the row has to mark it as one.
      resolvedFeedPointByCircuit: d.feedPointByCircuit,
      resolvedCableTypeByCircuit: d.cableTypeByCircuit,
      derivedTotalM: d.totalM,
      removedDeviceMarkers: d.removedDeviceMarkers,
      feedingBoardByCircuit: d.feedingBoardByCircuit,
      circuitBoardByCircuit: d.circuitBoardByCircuit,
      mainBoard: d.mainBoard,
      routeRequests: d.routeRequests,
      feedsByBoard: d.feedsByBoard,
      unfedCategories: d.unfedCategories,
      deviceCountsByBoard: d.deviceCountsByBoard,
      appCircuits: d.appCircuits,
      feederCircuits: d.feederCircuits,
      categoryByCircuit: d.categoryByCircuit,
      unfedDevices: d.unfedDevices,
      orphanedCircuitIds: d.orphanedDevices.circuitIds,
      orphanedDevices: {
        circuitCount: d.orphanedDevices.circuitCount,
        deviceCount: d.orphanedDevices.deviceCount,
        sourceLabels: d.orphanedDevices.sourceLabels,
      },
    };
  }, [
    baseGeometry,
    overrides,
    imageScaleFactor,
    scalePxPerM,
    systemByCircuit,
    routeCache.cache,
    trays,
    isSetupDone,
  ]);
  mainBoardRef.current = derived.mainBoard;
  const askRouter = useCallback<RequestReroute>(
    (request) => requestReroute.current(request),
    [],
  );
  const { routing } = useCircuitRouting({
    requests: derived.routeRequests,
    routeCache,
    floorPlanId,
    trayTypes,
    requestReroute: askRouter,
    isEnabled: areTrayInputsLoaded && isSetupDone,
  });
  const mainBoardView = useMemo(
    () =>
      describeMainBoard({
        state: derived.mainBoard,
        sources: derived.derivedGeometry?.sources ?? [],
        names: overrides.sourceNames ?? {},
      }),
    [derived.mainBoard, derived.derivedGeometry, overrides.sourceNames],
  );

  // Deliberately NOT dependent on `overrides`: it is the run's own geometry
  // measured our way, so it moves only when the plan or the scale does.
  const baselineTotalM = useMemo(
    () =>
      baseGeometry
        ? applyOverrides(
            baseGeometry,
            NO_OVERRIDES,
            imageScaleFactor,
            scalePxPerM,
          ).totalM
        : 0,
    [baseGeometry, imageScaleFactor, scalePxPerM],
  );

  feedingRef.current = derived.feedingBoardByCircuit;
  derivedRef.current = derived.derivedGeometry;
  billedBeforeRef.current = derived.billedByCircuit;
  traysRef.current = trays ?? [];
  trayTypesRef.current = [...new Set((trays ?? []).map((t) => t.type))].sort();
  wallsRef.current = walls ?? NO_FINGERPRINT_WALLS;
  hasDetectedWallsRef.current = hasDetectedWalls;

  const baseSources = baseGeometry?.sources ?? NO_SOURCES;

  const { boardHeights, installationHeights, trayHeights } = overrides;
  const storedHeights = useMemo(
    () => ({ boardHeights, installationHeights, trayHeights }),
    [boardHeights, installationHeights, trayHeights],
  );
  const heights = useMemo(() => resolveHeights(storedHeights), [storedHeights]);

  return {
    ...derived,
    baselineTotalM,
    rerouteCircuit,
    reroutingCircuitId,
    rerouteAllCircuits,
    isReroutingPlan,
    arePlanTraysStale,
    planRerouteSummary,
    dismissPlanRerouteSummary,
    heights,
    storedHeights,
    setHeight,
    heightsConfirmed: Boolean(overrides.heightsConfirmed),
    confirmHeights,
    trayHeightsConfirmed: Boolean(overrides.trayHeightsConfirmed),
    confirmTrayHeights,
    wallsConfirmed: Boolean(overrides.wallsConfirmed),
    confirmWalls,
    sourceOverrides: overrides.sourceOverrides ?? {},
    moveSource,
    resetSource,
    baseSources,
    removeSource,
    removeSourceTo,
    addSource,
    addedSources: overrides.addedSources ?? {},
    addCircuit,
    addedCircuits,
    removeCircuit,
    restoreSource,
    removedSources: overrides.removedSources ?? {},
    renameSource,
    sourceNames: overrides.sourceNames ?? {},
    // Only what the estimator set. The run's own cable type and board reach the
    // row on the BoQ item, and re-deriving either here is how the panel came to
    // disagree with the run once already.
    cableTypeByCircuit: derived.resolvedCableTypeByCircuit,
    setCableType,
    resetCableType,
    addedCableTypes: overrides.cableTypes ?? NO_CABLE_TYPES,
    addCableType,
    removeCableType,
    setLegCableType,
    setFeederFrom,
    setFeederOffFloor,
    feederOffFloor: overrides.feederOffFloor ?? NO_OFF_FLOOR,
    feedPointByCircuit: derived.resolvedFeedPointByCircuit,
    setFeedPoint,
    resetFeedPoint,
    areaFeeds: overrides.areaFeeds ?? NO_AREAS,
    addAreaFeed,
    removeAreaFeed,
    boardsConfirmed: Boolean(overrides.boardsConfirmed),
    isSetupDone,
    systemByCircuit,
    circuitBoardsChecked: overrides.circuitBoardsChecked ?? NO_CHECKED_BOARDS,
    checkCircuitBoard,
    uncheckCircuitBoard,
    confirmBoards,
    reopenBoards,
    mainBoard: derived.mainBoard,
    mainBoardView,
    setMainBoard,
    clearMainBoard,
    boardRoleAnswers: overrides.boardRoleAnswers ?? NO_ROLE_ANSWERS,
    setBoardRole,
    clearBoardRole,
    circuitBoardByCircuit: derived.circuitBoardByCircuit,
    assignDevices,
    assignDevicesBatch,
    circuitNames: overrides.circuitNames ?? NO_CIRCUIT_NAMES,
    circuitKeyOverrides: overrides.circuitKeys ?? NO_CIRCUIT_NAMES,
    setCircuitKey,
    setCircuitName,
    setBoardFeeds,
    clearBoardFeeds,
    storedBoardFeeds: overrides.boardFeeds,
    unbilledCategories: overrides.unbilledCategories ?? NO_CATEGORIES,
    setCategoryUnbilled,
    routing,
    routeOverrides: overrides.routeOverrides ?? {},
    setRoutePath,
    setRoutePaths,
    resetRoute,
    groupOverrides: overrides.groupOverrides ?? {},
    reassignDevice,
    reassignDevices,
    adoptDevice,
    adoptedDevices: overrides.adoptedDevices ?? EMPTY_ADOPTED,
    deviceMovedTo,
    resetGroup,
    removedDevices: overrides.removedDevices ?? {},
    removalGroups: overrides.removalGroups ?? EMPTY_REMOVAL_GROUPS,
    removeDevice,
    removeDevices,
    restoreDevice,
    restoreGroup,
    closeRemovalGroup,
    parkDevices,
    assignGroupTo,
    assignDeviceTo,
    resetAll,
    hasEdits: hasAnyOverride(overrides),
    undo,
    redo,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    undoStep,
    redoStep,
    steppedTo,
  };
}
